import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useConfirm } from "../../../components/feedback";
import { PageListContainer, PageCreateButton } from "../../../components/Page";
import DataTableWithPagination from "../../../components/DataTable";
import ProjectDialog from "../../../components/GenericDialog";
import PlansModel, { getPlanCols } from "../model";
import { subscriptionPlanService } from "../../../services/subscriptionPlanService";
import { planCreateFormSchema, planEditFormSchema } from "../../../lib/validation/planSchemas";
import createPlanBody from "./components/CreatePlanDialogBody";
import editPlanBody from "./components/EditPlanDialogBody";

const toNumber = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

function PlansPage() {
  const { t } = useTranslation();
  const cols = useMemo(() => getPlanCols(t), [t]);
  const datatable = useRef<any>(null);
  const createDialogRef = useRef<any>(null);
  const editDialogRef = useRef<any>(null);
  const confirm = useConfirm();

  useEffect(() => {
    const handleGlobalRefresh = () => {
      datatable.current?.refresh();
    };
    window.addEventListener("refreshDataTable", handleGlobalRefresh);
    return () => window.removeEventListener("refreshDataTable", handleGlobalRefresh);
  }, []);

  const showSnack = (opts: any) => {
    window.dispatchEvent(new CustomEvent("showGlobalNotification", { detail: opts }));
  };

  const createNewPlan = () => {
    createDialogRef.current?.showDialog(PlansModel.createDialogOpts(t));
  };

  const createPlanSubmitAction = async (formJson: any) => {
    try {
      await subscriptionPlanService.create({
        name: String(formJson.name || "").trim(),
        description: String(formJson.description || "").trim() || undefined,
        status: String(formJson.status || "ACTIVE") as "ACTIVE" | "PASSIVE",
        chatMessageLimits: {
          dailyLimit: toNumber(formJson.chatDailyLimit),
          weeklyLimit: toNumber(formJson.chatWeeklyLimit),
          monthlyLimit: toNumber(formJson.chatMonthlyLimit),
        },
        mailAnalysisLimits: {
          dailyLimit: toNumber(formJson.mailDailyLimit),
          weeklyLimit: toNumber(formJson.mailWeeklyLimit),
          monthlyLimit: toNumber(formJson.mailMonthlyLimit),
        },
      });
      PlansModel
        .message(PlansModel.SUCCESS, t("common.createRequest"), t("plans.createdSuccess"), Date.now)
        .then((opts) => {
          showSnack(opts);
          setTimeout(() => datatable.current?.refresh(), 500);
        });
      return true;
    } catch (e: any) {
      console.error(e);
      PlansModel
        .message(
          PlansModel.ERROR,
          t("common.createRequest"),
          e?.response?.data?.message || t("common.requestFailed"),
          Date.now
        )
        .then((opts) => showSnack(opts));
      return false;
    }
  };

  const editPlanSubmitAction = async (formJson: any) => {
    try {
      await subscriptionPlanService.update({
        id: String(formJson.id || ""),
        name: String(formJson.name || "").trim(),
        description: String(formJson.description || "").trim() || undefined,
        status: String(formJson.status || "ACTIVE") as "ACTIVE" | "PASSIVE",
        chatMessageLimits: {
          dailyLimit: toNumber(formJson.chatDailyLimit),
          weeklyLimit: toNumber(formJson.chatWeeklyLimit),
          monthlyLimit: toNumber(formJson.chatMonthlyLimit),
        },
        mailAnalysisLimits: {
          dailyLimit: toNumber(formJson.mailDailyLimit),
          weeklyLimit: toNumber(formJson.mailWeeklyLimit),
          monthlyLimit: toNumber(formJson.mailMonthlyLimit),
        },
      });
      PlansModel
        .message(PlansModel.SUCCESS, t("common.updateRequest"), t("plans.updatedSuccess"), Date.now)
        .then((opts) => {
          showSnack(opts);
          setTimeout(() => datatable.current?.refresh(), 500);
        });
      return true;
    } catch (e: any) {
      console.error(e);
      PlansModel
        .message(
          PlansModel.ERROR,
          t("common.updateRequest"),
          e?.response?.data?.message || t("common.requestFailed"),
          Date.now
        )
        .then((opts) => showSnack(opts));
      return false;
    }
  };

  const handleDelete = async (item: any) => {
    return new Promise((resolve) => {
      confirm({ description: t("plans.deleteConfirm", { name: item.name || item.id }) })
        .then(() => {
          subscriptionPlanService.delete(item.id).then(() => resolve({}));
        })
        .then(() => {
          PlansModel
            .message(PlansModel.SUCCESS, t("common.deleteRequest"), t("plans.deletedSuccess"), Date.now)
            .then((opts) => {
              showSnack(opts);
              setTimeout(() => datatable.current?.refresh(), 500);
            });
        })
        .catch((e: unknown) => {
          console.error(e);
        });
    });
  };

  const handleEdit = async (item: any) => {
    editDialogRef.current?.showDialog(PlansModel.editDialogOpts(t), item);
  };

  return (
    <PageListContainer>
      <ProjectDialog
        ref={createDialogRef}
        submitAction={createPlanSubmitAction}
        body={createPlanBody}
        validationSchema={planCreateFormSchema}
      />
      <ProjectDialog
        ref={editDialogRef}
        submitAction={editPlanSubmitAction}
        body={editPlanBody}
        validationSchema={planEditFormSchema}
      />

      <DataTableWithPagination
        ref={datatable}
        cols={cols}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        fetchUrl="/subscription-plan/read"
        searchKey="name"
        headerActions={<PageCreateButton onClick={createNewPlan}>{t("plans.new")}</PageCreateButton>}
      />
    </PageListContainer>
  );
}

export default PlansPage;

